import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import DepartementCountBadge from "./DepartementCountBadge";

const STORAGE_KEY = "sidebar:openGroups";

const NavItem = ({ item, pathname, onNavClick }) => {
  const isActive = pathname === item.path;
  return (
    <Link
      to={item.path}
      onClick={onNavClick}
      className="flex items-center gap-2.5 rounded-lg text-sm transition-all"
      style={{
        minHeight: "40px",
        padding: "0 10px",
        backgroundColor: isActive ? "rgba(94,106,210,0.12)" : "transparent",
        color: isActive ? "var(--lm-ink)" : "var(--lm-ink-2)",
        borderLeft: isActive ? "2px solid #5e6ad2" : "2px solid transparent",
        marginLeft: "-2px",
      }}
      onMouseEnter={(e) => {
        if (!isActive) {
          e.currentTarget.style.backgroundColor = "var(--lm-s2)";
          e.currentTarget.style.color = "var(--lm-ink)";
        }
      }}
      onMouseLeave={(e) => {
        if (!isActive) {
          e.currentTarget.style.backgroundColor = "transparent";
          e.currentTarget.style.color = "var(--lm-ink-2)";
        }
      }}
    >
      <item.icon
        className="flex-shrink-0"
        style={{ width: "17px", height: "17px", color: isActive ? "#5e6ad2" : "var(--lm-ink-3)" }}
      />
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <span className="font-medium truncate" style={{ fontSize: "14.5px" }}>{item.title}</span>
        {item.badge && (
          <span
            className="flex-shrink-0"
            style={{
              fontSize: "10px", fontWeight: 600, letterSpacing: "0.3px",
              padding: "2px 7px", borderRadius: "9999px",
              backgroundColor: "rgba(94,106,210,0.15)", color: "#828fff",
            }}
          >
            {item.badge}
          </span>
        )}
      </div>
    </Link>
  );
};

const SubNavItem = ({ subItem, pathname, onNavClick }) => {
  const isActive = pathname === subItem.path;
  return (
    <Link
      to={subItem.path}
      onClick={onNavClick}
      className="flex items-center gap-2.5 rounded-lg text-sm transition-all"
      style={{
        minHeight: "40px",
        padding: "0 10px",
        backgroundColor: isActive ? "rgba(94,106,210,0.12)" : "transparent",
        color: isActive ? "var(--lm-ink)" : "var(--lm-ink-2)",
        borderLeft: isActive ? "2px solid #5e6ad2" : "2px solid transparent",
        marginLeft: "-2px",
      }}
      onMouseEnter={(e) => {
        if (!isActive) {
          e.currentTarget.style.backgroundColor = "var(--lm-s2)";
          e.currentTarget.style.color = "var(--lm-ink)";
        }
      }}
      onMouseLeave={(e) => {
        if (!isActive) {
          e.currentTarget.style.backgroundColor = "transparent";
          e.currentTarget.style.color = "var(--lm-ink-2)";
        }
      }}
    >
      <subItem.icon
        className="flex-shrink-0"
        style={{ width: "17px", height: "17px", color: isActive ? "#5e6ad2" : "var(--lm-ink-3)" }}
      />
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <span className="font-medium truncate flex items-center gap-1" style={{ fontSize: "14.5px" }}>
          {subItem.title}
          {subItem.key === "organigramme" && <DepartementCountBadge />}
        </span>
        {subItem.badge && (
          <span
            className="flex-shrink-0"
            style={{
              fontSize: "10px", fontWeight: 600, letterSpacing: "0.3px",
              padding: "2px 7px", borderRadius: "9999px",
              backgroundColor: "rgba(94,106,210,0.15)", color: "#828fff",
            }}
          >
            {subItem.badge}
          </span>
        )}
      </div>
    </Link>
  );
};

const GroupHeader = ({ label, open, hasActive, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-expanded={open}
    className="w-full flex items-center justify-between transition-colors"
    style={{
      fontSize: "11px",
      fontWeight: 600,
      letterSpacing: "0.6px",
      textTransform: "uppercase",
      color: "var(--lm-ink-3)",
      padding: "6px 10px",
      marginBottom: "3px",
      background: "transparent",
      border: "none",
      cursor: "pointer",
    }}
    onMouseEnter={(e) => { e.currentTarget.style.color = "var(--lm-ink-2)"; }}
    onMouseLeave={(e) => { e.currentTarget.style.color = "var(--lm-ink-3)"; }}
  >
    <span className="flex items-center gap-1.5 truncate">
      {label}
      {/* Accent dot when the active page lives inside a collapsed group */}
      {!open && hasActive && (
        <span
          style={{
            width: "5px", height: "5px", borderRadius: "9999px",
            backgroundColor: "#5e6ad2", flexShrink: 0,
          }}
        />
      )}
    </span>
    <ChevronDown
      className="flex-shrink-0 transition-transform"
      style={{
        width: "15px",
        height: "15px",
        transform: open ? "rotate(0deg)" : "rotate(-90deg)",
      }}
    />
  </button>
);

const SidebarNavMenu = ({ menuItems, pathname, onNavClick }) => {
  // Separate top-level items (no sub-items) from collapsible groups
  const topItems = menuItems.filter((item) => !item.items);
  const groups = menuItems.filter((item) => item.items && item.items.length > 0);

  // Which group contains the currently active route
  const activeGroupKey = groups.find((g) => g.items.some((it) => it.path === pathname))?.key;

  const [openGroups, setOpenGroups] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    } catch {
      return {};
    }
  });

  // Always reveal the group that holds the active route
  useEffect(() => {
    if (activeGroupKey) {
      setOpenGroups((prev) => (prev[activeGroupKey] ? prev : { ...prev, [activeGroupKey]: true }));
    }
  }, [activeGroupKey]);

  // Persist user's expand/collapse choices
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(openGroups));
    } catch {
      /* ignore quota / privacy-mode errors */
    }
  }, [openGroups]);

  const toggleGroup = (key) => setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  const isOpen = (group) =>
    group.key in openGroups ? openGroups[group.key] : group.key === activeGroupKey;

  return (
    <nav>
      {/* Top-level standalone items (if any remain) */}
      {topItems.length > 0 && (
        <div className="space-y-0.5 mb-4">
          {topItems.map((item) => (
            <NavItem key={item.key} item={item} pathname={pathname} onNavClick={onNavClick} />
          ))}
        </div>
      )}

      {/* Collapsible grouped sections */}
      {groups.map((group, i) => {
        const open = isOpen(group);
        const hasActive = group.key === activeGroupKey;
        return (
          <div key={group.key} style={{ marginBottom: i < groups.length - 1 ? "8px" : 0 }}>
            <GroupHeader
              label={group.title}
              open={open}
              hasActive={hasActive}
              onToggle={() => toggleGroup(group.key)}
            />
            {open && (
              <div className="space-y-0.5" style={{ marginBottom: "8px" }}>
                {group.items.map((subItem) => (
                  <SubNavItem
                    key={subItem.key}
                    subItem={subItem}
                    pathname={pathname}
                    onNavClick={onNavClick}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
};

export default SidebarNavMenu;
